import { RyeIcon } from "../rye/RyeIcon";
import { RYE_PANEL_SURFACE } from "../rye/RyeScreenWrapper";
import { buildClasses } from "../utils/buildClasses";
import breadLogo from "../images/bread_logo_192.png";

// The window the feature tour's mocks live inside: the 16:9 card and the nav
// rail down its left edge. Shared so that every screen sits in an identical
// shell -- the tour crossfades one into the next, and any difference in the
// frame would show up as a doubled border mid-fade.
//
// Metrics are copied from the real app. If you change it, change these:
//   nav rail   w-[3.25rem], pl-2 pr-2, justify-between, pt-[9px] above the
//              logo, h-5 below it, h-3 between items (MainNavMenu)
//   nav item   p-2, 1.2rem icon, filled and purple on a raised tab when
//              selected (MainNavButton)
//   panel      RYE_PANEL_SURFACE, 8px from the window's edges (MainNav)

export type MockNavTab =
  | "home"
  | "transactions"
  | "balances"
  | "cash-flow"
  | "budgets";

// MAIN_NAV_DEFINITIONS' icons, in MAIN_NAV_ORDER.
const NAV_TABS: { tab: MockNavTab; icon: string }[] = [
  { tab: "home", icon: "home" },
  { tab: "transactions", icon: "checklist_rtl" },
  { tab: "balances", icon: "savings" },
  { tab: "cash-flow", icon: "bar_chart" },
  { tab: "budgets", icon: "map" },
];

function NavButton(props: { icon: string; selected?: boolean }): JSX.Element {
  const selected = props.selected === true;
  return (
    <div
      className={buildClasses("relative", "isolate", "flex", "p-2", {
        if: selected,
        then: "text-purple",
        else: "text-on-surface-500",
      })}
    >
      {/* The selected item's raised tab, styled like a panel. */}
      {selected && (
        <span
          aria-hidden
          className={buildClasses(
            "absolute",
            "inset-0",
            "-z-10",
            "rounded-lg",
            "border",
            "bg-surface",
            "border-edge/[0.12]",
            "border-t-edge/[0.08]",
            "border-b-edge/[0.2]",
            "shadow-main-content",
          )}
        />
      )}
      <RyeIcon
        name={props.icon}
        fill={selected}
        size="custom"
        className="text-[1.2rem]"
      />
    </div>
  );
}

function NavSpacer(): JSX.Element {
  return <div className={buildClasses("h-3")} />;
}

// A closed RyeMonthPicker or RyeDropDown at size sm: a bordered box with its
// value and a chevron.
export function MockSelect(props: {
  children: string;
  widthClassName: string;
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        "flex",
        "h-7",
        "items-center",
        "justify-between",
        "rounded-md",
        "border",
        "border-on-surface-300",
        "px-3",
        "text-sm",
        "text-on-surface-800",
        props.widthClassName,
      )}
    >
      <span className={buildClasses("truncate")}>{props.children}</span>
      <RyeIcon
        name="keyboard_arrow_down"
        size="sm"
        className={buildClasses("text-on-surface-500")}
      />
    </div>
  );
}

/**
 * A screen's panel, as RyeScreenWrapper draws it: the app's warm surface,
 * outline, and shadow. Its contents clip to its rounded corners.
 */
export function MockPanel(props: {
  /** A fixed width, such as a sidebar's; otherwise the panel fills the row. */
  widthClassName?: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        "relative",
        "flex",
        "min-w-0",
        props.widthClassName
          ? buildClasses(props.widthClassName, "shrink-0")
          : "flex-1",
        RYE_PANEL_SURFACE,
      )}
    >
      {/* Clipped inside, not on the panel, which would clip its own shadow. */}
      <div
        className={buildClasses(
          "flex",
          "min-w-0",
          "flex-1",
          "overflow-hidden",
          "rounded-[inherit]",
          props.className ?? "",
        )}
      >
        {props.children}
      </div>
    </div>
  );
}

export function MockAppFrame(props: {
  // Which nav tab reads as the current screen.
  activeTab: MockNavTab;
  children: React.ReactNode;
}): JSX.Element {
  return (
    // 16:9 to match the proportions of a laptop screen (and Linear's hero
    // visual, which is exactly 1440x810). Content is sized to fill it; anything
    // past the fold clips, which reads as a scrolled table. A 16:9 frame at
    // phone widths would be ~210px tall and clip the stacked rows to nothing,
    // so phones get a portrait frame instead.
    <div
      className={buildClasses(
        "aspect-[3/4]",
        "sm:aspect-[16/9]",
        "mock-desktop:aspect-[16/9]",
        "w-full",
        "overflow-hidden",
        "rounded-2xl",
        "border",
        "border-edge/[0.12]",
        "bg-base",
        // The same drop shadow as the app mock on chatgpt.com/overview, which
        // the tour's design follows, copied from their computed style. Kept as
        // a drop-shadow filter rather than the box-shadow with the same
        // numbers: the two blur by different amounts, and the box-shadow came
        // out visibly tighter than theirs. The filter also makes the frame a
        // stacking context, so panels' shadows paint above its background.
        "[filter:drop-shadow(0_16px_12px_rgba(0,0,0,0.14))]",
        // MainNav's own padding.
        "pb-2",
        "pr-2",
        "pt-2",
        "flex",
      )}
    >
      <div
        className={buildClasses(
          "hidden",
          "sm:flex",
          "mock-desktop:flex",
          "w-[3.25rem]",
          "shrink-0",
          "grow-0",
          "flex-col",
          "justify-between",
          "pl-2",
          "pr-2",
        )}
      >
        <div className={buildClasses("flex", "flex-col")}>
          <div className={buildClasses("pt-[9px]", "pl-1")}>
            <img src={breadLogo} className={buildClasses("w-7")} alt="" />
          </div>
          <div className={buildClasses("h-5")} />
          {NAV_TABS.map(({ tab, icon }, index) => (
            <div key={tab}>
              {index > 0 && <NavSpacer />}
              <NavButton icon={icon} selected={props.activeTab === tab} />
            </div>
          ))}
        </div>
        <div>
          <NavButton icon="settings" />
          <NavSpacer />
          <NavButton icon="sync" />
        </div>
      </div>
      {props.children}
    </div>
  );
}
